/**
 * CFO Cockpit → P&L. Company-level Profit & Loss straight from the
 * QuickBooks report API: monthly income vs total costs vs net, the
 * expense category breakdown and year-on-year comparison. Plus the
 * board-workbook cascade (Revenue → Gross profit → Contribution →
 * Overheads → EBITDA) per quarter and the rolling LTM trend.
 */
import { useState } from "react";
import axios from "axios";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, ReferenceLine,
  ResponsiveContainer, Legend, Tooltip as RechartsTooltip,
} from "recharts";
import { Loader2 } from "lucide-react";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip, InfoTip } from "./cfoShared";
import { useQboData, QboHeader, fmtGbp, fmtMonthKey } from "./qboShared";

const API = process.env.REACT_APP_BACKEND_URL;

const yoyBadge = (cur, prev) => {
  if (prev == null || !prev) return null;
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  return `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}% vs prior year`;
};

const fmtW = (v) => (v == null ? "–" : `£${Math.round(v).toLocaleString("en-GB")}`);
const fmtQ = (q) => `${q.slice(5)} ${q.slice(2, 4)}`;

const CASCADE_ROWS = [
  { k: "revenue", label: "Revenue", bold: true },
  { k: "cos", label: "Cost of sales", neg: true },
  { k: "gp", label: "Gross profit", bold: true, margin: "gp_margin_pct" },
  { k: "people", label: "Direct people", neg: true },
  { k: "contribution", label: "Contribution", bold: true, margin: "contribution_margin_pct" },
  { k: "overheads", label: "Overheads", neg: true },
  { k: "ebitda", label: "EBITDA", bold: true, margin: "ebitda_margin_pct" },
];

const RANGE_STATS = [
  { k: "income", label: "Income" },
  { k: "costs", label: "Total costs" },
  { k: "investment", label: "Investment result" },
  { k: "net", label: "Net profit" },
];

function RangeProfitCard() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(`${today.slice(0, 4)}-01-01`);
  const [end, setEnd] = useState(today);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.get(
        `${API}/api/quickbooks/pnl-range`,
        { params: { start, end } },
      );
      setRes(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not fetch that period from QuickBooks.");
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  const cur = res?.current;
  const prior = res?.prior;
  return (
    <Card testid="pnl-range-card">
      <PanelTitle>Operating profit between two dates <InfoTip id="pnl_operating" /></PanelTitle>
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <label className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
          From
          <input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
            data-testid="pnl-range-start"
            className="block mt-1 text-xs text-slate-800 border border-stone-200 rounded-md px-2 py-1.5 bg-white" />
        </label>
        <label className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
          To
          <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
            data-testid="pnl-range-end"
            className="block mt-1 text-xs text-slate-800 border border-stone-200 rounded-md px-2 py-1.5 bg-white" />
        </label>
        <button type="button" onClick={run} disabled={busy || !start || !end}
          data-testid="pnl-range-calc-btn"
          className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#1B4F8A] text-white rounded-md px-3 py-2 hover:bg-[#173f6e] disabled:opacity-50">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {busy ? "Fetching…" : "Calculate"}
        </button>
      </div>
      {err && <p className="text-xs text-rose-600 mt-3" data-testid="pnl-range-error">{err}</p>}
      {cur && (
        <div className="mt-4 border-t border-stone-100 pt-4">
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3">
            <div data-testid="pnl-range-operating">
              <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">Operating profit</p>
              <p className={`text-2xl font-semibold font-mono tabular-nums ${cur.operating < 0 ? "text-rose-600" : "text-teal-700"}`}>
                {fmtGbp(cur.operating)}
              </p>
              <p className="text-[10px] text-stone-400">
                {cur.operating_margin_pct != null ? `${cur.operating_margin_pct}% of income` : ""}
                {prior && (yoyBadge(cur.operating, prior.operating) ? ` · ${yoyBadge(cur.operating, prior.operating)}` : "")}
              </p>
            </div>
            {RANGE_STATS.map(({ k, label }) => (
              <div key={k}>
                <p className="text-[10px] font-mono uppercase tracking-wider text-stone-400">{label}</p>
                <p className="text-sm font-mono tabular-nums text-slate-800">{fmtGbp(cur[k])}</p>
                {prior && <p className="text-[10px] text-stone-400">LY {fmtGbpShort(prior[k])}</p>}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-stone-400 mt-3">
            {res.start} to {res.end}, straight from the QuickBooks P&L. Operating profit excludes the
            below-the-line investment result. LY figures cover the same window one year earlier.
          </p>
        </div>
      )}
    </Card>
  );
}

function CascadeCard({ story }) {
  const quarters = (story?.quarters || []).slice(-5);
  if (!quarters.length) return null;
  const latestFull = [...quarters].reverse().find((q) => !q.partial && q.ly);
  return (
    <Card testid="pnl-cascade-card">
      <PanelTitle>The P&amp;L cascade · quarterly <InfoTip id="pnl_cascade" /></PanelTitle>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-xs min-w-[560px]">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
              <th className="text-left pb-1.5"> </th>
              {quarters.map((q) => (
                <th key={q.quarter} className="text-right pb-1.5">
                  {fmtQ(q.quarter)}{q.partial ? " (QTD)" : ""}
                </th>
              ))}
              <th className="text-right pb-1.5">{latestFull ? `${fmtQ(latestFull.quarter)} vs LY` : "vs LY"}</th>
            </tr>
          </thead>
          <tbody>
            {CASCADE_ROWS.map((row) => {
              const lyDelta = latestFull && latestFull.ly?.[row.k] != null && latestFull.ly[row.k]
                ? (100 * (latestFull[row.k] - latestFull.ly[row.k])) / Math.abs(latestFull.ly[row.k])
                : null;
              const good = row.neg ? lyDelta != null && lyDelta <= 0 : lyDelta != null && lyDelta >= 0;
              return (
                <tr key={row.k} className={`border-b border-stone-50 ${row.bold ? "bg-stone-50/60" : ""}`} data-testid={`pnl-cascade-${row.k}`}>
                  <td className={`py-1.5 ${row.bold ? "font-semibold text-slate-900" : "text-stone-500"}`}>{row.label}</td>
                  {quarters.map((q) => (
                    <td key={q.quarter} className="py-1.5 text-right font-mono tabular-nums">
                      <span className={row.bold ? "font-semibold text-slate-900" : "text-stone-600"}>
                        {row.neg ? `−${fmtW(q[row.k]).slice(1)}` : fmtW(q[row.k])}
                      </span>
                      {row.margin && q[row.margin] != null && (
                        <span className="block text-[9px] text-stone-400">{q[row.margin]}%</span>
                      )}
                    </td>
                  ))}
                  <td className={`py-1.5 text-right font-mono tabular-nums ${lyDelta == null ? "text-stone-300" : good ? "text-teal-700" : "text-rose-600"}`}>
                    {lyDelta == null ? "–" : `${lyDelta >= 0 ? "▲" : "▼"} ${Math.abs(lyDelta).toFixed(1)}%`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-stone-400 mt-2">
        Cost of sales = project costs (5xxx accounts). Direct people = delivery salaries inside cost of sales
        (6xxx). Overheads = 7xxx expenses, depreciation added back to EBITDA. Margins are % of revenue.
      </p>
    </Card>
  );
}

function LtmCard({ story }) {
  const ltm = (story?.ltm || []).map((r) => ({ ...r, label: fmtMonthKey(r.month) }));
  if (!ltm.length) return null;
  return (
    <Card testid="pnl-ltm-card">
      <PanelTitle>Rolling 12 months · LTM trend <InfoTip id="pnl_ltm" /></PanelTitle>
      <div className="h-64 mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={ltm} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} interval={3} />
            <YAxis yAxisId="v" tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={56} />
            <YAxis yAxisId="m" orientation="right" domain={[0, 50]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={36} />
            <RechartsTooltip
              contentStyle={{ fontSize: 11, borderRadius: 8 }}
              formatter={(v, n) =>
                n === "ltm_ebitda_margin_pct"
                  ? [`${v}%`, "EBITDA margin"]
                  : [fmtW(v), n === "ltm_revenue" ? "LTM Revenue" : "LTM EBITDA"]
              }
            />
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(v) => (v === "ltm_revenue" ? "LTM Revenue" : v === "ltm_ebitda" ? "LTM EBITDA" : "EBITDA margin %")}
            />
            <Line yAxisId="v" type="monotone" dataKey="ltm_revenue" stroke="#1B4F8A" strokeWidth={2} dot={false} />
            <Line yAxisId="v" type="monotone" dataKey="ltm_ebitda" stroke="#0D9488" strokeWidth={2} dot={false} />
            <Line yAxisId="m" type="monotone" dataKey="ltm_ebitda_margin_pct" stroke="#d6a419" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-stone-400 mt-2">
        Each point is the sum of the twelve months ending there - the smoothed view of a lumpy business,
        and the lens the Big 5 valuation metrics use.
      </p>
    </Card>
  );
}

export default function PnlTab() {
  const { data, loading, error, refreshing, reload } = useQboData("pnl");
  const { data: story } = useQboData("story");
  if (loading || error) {
    return <TabState loading={loading} error={error} testid="pnl-state" />;
  }
  const k = data.kpis || {};
  const prior = data.prior || {};
  const hasSplit = k.operating_12m != null;
  const months = (data.months || []).map((m) => ({
    ...m,
    costs: (m.expenses || 0) + (m.cogs || 0),
  }));
  const totalCosts = k.costs_12m || 0;
  const cats = (data.categories || []).filter((c) => c.total !== 0);
  const invLines = (data.investment_lines || []).filter((l) => l.total !== 0);
  const otherLines = (data.other_nonop_lines || []).filter((l) => l.total !== 0);

  const kpiCards = hasSplit
    ? [
        {
          label: "Income · 12m",
          value: fmtGbpShort(k.income_12m),
          sub: yoyBadge(k.income_12m, prior.income) || "accrual basis",
          testid: "pnl-kpi-income",
          info: "pnl_income",
        },
        {
          label: "Total costs · 12m",
          value: fmtGbpShort(k.costs_12m),
          sub: yoyBadge(k.costs_12m, prior.costs) || "expenses + cost of sales",
          testid: "pnl-kpi-costs",
        },
        {
          label: "Operating profit · 12m",
          value: fmtGbpShort(k.operating_12m),
          sub:
            (k.operating_margin_pct != null ? `${k.operating_margin_pct}% of income · ` : "") +
            (yoyBadge(k.operating_12m, prior.operating) || "before investments"),
          tone: k.operating_12m < 0 ? "text-rose-600" : "text-teal-700",
          testid: "pnl-kpi-operating",
          info: "pnl_operating",
        },
        {
          label: "Investment result · 12m",
          value: fmtGbpShort(k.investment_12m),
          sub: "portfolios, ventures and interest · below the line",
          tone: k.investment_12m < 0 ? "text-rose-600" : "text-[#1B4F8A]",
          testid: "pnl-kpi-investment",
          info: "pnl_investment",
        },
        {
          label: "Net profit · 12m",
          value: fmtGbpShort(k.net_12m),
          sub: yoyBadge(k.net_12m, prior.net) || "operating + investments",
          tone: k.net_12m < 0 ? "text-rose-600" : "text-slate-900",
          testid: "pnl-kpi-net",
          info: "pnl_net",
        },
      ]
    : [
        {
          label: "Income · 12m",
          value: fmtGbpShort(k.income_12m),
          sub: yoyBadge(k.income_12m, prior.income) || "accrual basis",
          testid: "pnl-kpi-income",
          info: "pnl_income",
        },
        {
          label: "Total costs · 12m",
          value: fmtGbpShort(k.costs_12m),
          sub: yoyBadge(k.costs_12m, prior.costs) || "expenses + cost of sales",
          testid: "pnl-kpi-costs",
        },
        {
          label: "Net profit · 12m",
          value: fmtGbpShort(k.net_12m),
          sub: yoyBadge(k.net_12m, prior.net) || "",
          tone: k.net_12m < 0 ? "text-rose-600" : "text-teal-700",
          testid: "pnl-kpi-net",
          info: "pnl_net",
        },
        {
          label: "Net margin",
          value: k.net_margin_pct != null ? `${k.net_margin_pct}%` : "–",
          sub: "net ÷ income",
          tone:
            k.net_margin_pct != null && k.net_margin_pct < 0
              ? "text-rose-600"
              : "text-slate-900",
          testid: "pnl-kpi-margin",
        },
      ];

  return (
    <div className="flex flex-col gap-6" data-testid="pnl-tab">
      <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="pnl-refresh-btn" />

      <KpiStrip cards={kpiCards} />

      <RangeProfitCard />

      <CascadeCard story={story} />
      <LtmCard story={story} />

      <Card testid="pnl-chart-card">
        <PanelTitle>
          Income vs costs vs net · monthly <InfoTip id="pnl_income" />
        </PanelTitle>
        <div className="h-64 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tickFormatter={fmtMonthKey} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={58} />
              <RechartsTooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                labelFormatter={fmtMonthKey}
                formatter={(v, n) => [
                  fmtGbp(v),
                  n === "income" ? "Income" : n === "costs" ? "Costs"
                    : n === "operating" ? "Operating profit" : "Net",
                ]}
              />
              <Legend
                wrapperStyle={{ fontSize: 10 }}
                formatter={(v) =>
                  v === "income" ? "Income" : v === "costs" ? "Costs"
                    : v === "operating" ? "Operating profit" : "Net profit"
                }
              />
              <ReferenceLine y={0} stroke="#e7e5e4" />
              <Bar dataKey="income" fill="#1B4F8A" maxBarSize={20} radius={[3, 3, 0, 0]} />
              <Bar dataKey="costs" fill="#d6a419" maxBarSize={20} radius={[3, 3, 0, 0]} />
              {hasSplit && (
                <Line type="monotone" dataKey="operating" stroke="#1B4F8A" strokeWidth={1.5} strokeDasharray="5 3" dot={false} />
              )}
              <Line type="monotone" dataKey="net" stroke="#0D9488" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {hasSplit && (
          <p className="text-[10px] text-stone-400 mt-2">
            The dashed line is operating profit (income minus costs). The gap between it and net profit
            is the below-the-line investment result plus any other non-operating items.
          </p>
        )}
      </Card>

      {hasSplit && (invLines.length > 0 || otherLines.length > 0) && (
        <Card testid="pnl-investment-card">
          <PanelTitle>Below the line · investments vs other non-operating <InfoTip id="pnl_investment" /></PanelTitle>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">
                Investment result · {fmtGbpShort(k.investment_12m)} (12m)
              </p>
              {invLines.length === 0 ? (
                <p className="text-xs text-stone-400">No investment lines in the period.</p>
              ) : (
                <ul className="space-y-1">
                  {invLines.map((l) => (
                    <li key={l.name} className="flex items-baseline justify-between gap-3 text-xs" data-testid={`pnl-inv-line-${l.name}`}>
                      <span className="text-slate-700 truncate">{l.name}</span>
                      <span className={`font-mono tabular-nums shrink-0 ${l.total < 0 ? "text-rose-600" : "text-slate-900"}`}>
                        {fmtGbpShort(l.total)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">
                Other non-operating · {fmtGbpShort(k.other_nonop_12m)} (12m)
              </p>
              {otherLines.length === 0 ? (
                <p className="text-xs text-stone-400">Nothing else below the line.</p>
              ) : (
                <ul className="space-y-1">
                  {otherLines.map((l) => (
                    <li key={l.name} className="flex items-baseline justify-between gap-3 text-xs" data-testid={`pnl-other-line-${l.name}`}>
                      <span className="text-slate-700 truncate">{l.name}</span>
                      <span className={`font-mono tabular-nums shrink-0 ${l.total < 0 ? "text-rose-600" : "text-slate-900"}`}>
                        {fmtGbpShort(l.total)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <p className="text-[10px] text-stone-400 mt-3">
            Classified by account name from the QuickBooks Other Income and Other Expenses sections.
            Operating profit + investment result + other non-operating = net profit.
          </p>
        </Card>
      )}

      <Card testid="pnl-categories-card">
        <PanelTitle>Where the money goes · expense categories (12m)</PanelTitle>
        {cats.length === 0 ? (
          <p className="text-xs text-stone-400 py-2">No expense lines in the period.</p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {cats.map((c) => {
              const share = totalCosts > 0 ? (100 * c.total) / totalCosts : 0;
              return (
                <li key={c.name} className="flex items-center gap-3" data-testid={`pnl-cat-${c.name}`}>
                  <span className="text-xs text-slate-800 w-56 truncate shrink-0">{c.name}</span>
                  <span className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden">
                    <span
                      className="block h-full rounded-full bg-[#1B4F8A]/70"
                      style={{ width: `${Math.min(Math.max(share, 1), 100)}%` }}
                    />
                  </span>
                  <span className="text-xs font-mono tabular-nums text-slate-900 w-20 text-right">
                    {fmtGbpShort(c.total)}
                  </span>
                  <span className="text-[10px] font-mono text-stone-400 w-10 text-right">
                    {share.toFixed(0)}%
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
